import { TypedDocumentNode, gql } from '@apollo/client';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RewardsFlow } from 'components/so5/LastFixtureStoryReworked/RewardsFlow';
import { useRewardsVerification } from 'components/so5/LastFixtureStoryReworked/RewardsRecap/useRewardsVerification';

import { NewRewardsBanner_so5Reward } from './__generated__/index.graphql';
import rewardsBannerBg from './assets/rewards-banner-bg.jpg';

const Root = styled.button`
  display: block;
  width: 100%;
  background: no-repeat left / cover url(${rewardsBannerBg});
  padding: var(--triple-unit);
  border: 2px solid var(--c-white);
  border-radius: var(--double-unit);

  @media ${tabletAndAbove} {
    background-position: center;
  }
`;
const NotAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;

type Props = {
  so5Rewards: NewRewardsBanner_so5Reward[];
  Wrapper?: FC<{ children: ReactNode }>;
};
export const NewRewardsBanner = ({ so5Rewards, Wrapper }: Props) => {
  const { claimableRewardsByType } = useRewardsVerification(so5Rewards);
  const [showRewardsFlow, setShowRewardsFlow] = useState(false);

  const content = (
    <Root onClick={() => setShowRewardsFlow(true)}>
      <SBHorizontal>
        <NotAlignedVertical gap={0}>
          <LabelL bold>
            <FormattedMessage
              id="RewardsBanner.title"
              defaultMessage="New Rewards"
            />
          </LabelL>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              id="RewardsBanner.subtitle"
              defaultMessage="Your results are in!"
            />
          </LabelM>
        </NotAlignedVertical>
        <Button size="medium" asSpan>
          <FormattedMessage
            id="RewardsBanner.cta"
            defaultMessage="Claim rewards"
          />
        </Button>
      </SBHorizontal>
    </Root>
  );

  return (
    <>
      {showRewardsFlow && (
        <RewardsFlow
          initialStep="rewardsRecap"
          so5Rewards={so5Rewards}
          onClose={() => setShowRewardsFlow(false)}
        />
      )}
      {claimableRewardsByType.total > 0 && (
        <>{Wrapper ? <Wrapper>{content}</Wrapper> : content}</>
      )}
    </>
  );
};

NewRewardsBanner.fragments = {
  so5Reward: gql`
    fragment NewRewardsBanner_so5Reward on So5Reward {
      slug
      id
      ...RewardsFlow_so5Reward
    }
    ${RewardsFlow.fragments.so5Reward}
  ` as TypedDocumentNode<NewRewardsBanner_so5Reward>,
};
